import { Box, Button, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import Alert from '@mui/material/Alert';

import { licensePlateOptions } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/CommonOptions';
import {
    ReactHookFormDropdownSelect,
    ReactHookFormSelectOption,
} from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/ReactHookFormSelect';
import { ReactHookFormTextField } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/ReactHookFormTextField';
import {
    Localized,
    useLanguage,
    useTranslation,
} from 'dg-web-shared/lib/Localization';
import { VehicleLicensePlateType } from 'dg-web-shared/common/models/Vehicle';
import { useEasyForm } from 'dg-web-shared/common/utils/FormHooksUtils';
import {
    RequestStatus,
    ServerRequestState,
} from 'dg-web-shared/lib/hooks/ServerStateHooks';
import { CancelSave } from '../layout/CancelSave';
import { useNavigate } from 'react-router';
import { BackendRequestErrorMessage } from 'dg-web-shared/common/components/material-ui/BackendRequestErrorMessage';
import { FeedbackPopup } from '../FeedbackPopup';
import { useEffect, useState } from 'react';
import { ValidationData } from 'dg-web-shared/lib/forms/FormValidationHelpers';
import { GenericFormSubmitError } from '../../shared/GenericFormSubmitError.tsx';
import { useCustomerContext } from '../CustomerProvider.tsx';
import { parkingaboBadgeErrorText } from 'dg-web-shared/lib/RfidCardValidation.ts';
import { Control, UseFormSetValue } from 'react-hook-form';
import { Language } from 'dg-web-shared/lib/Text.ts';
import { mandatoryFieldErrorMessage } from 'dg-web-shared/common/components/translations/FormValidationErrorTexts.ts';
import { VehicleIdentification } from 'product-shared/parkingabo/ParkingaboOperatingMode.tsx';
import { ParkingaboContactInfo } from '../../routes/Help.tsx';

enum Fields {
    Description = 'description',
    LicensePlate = 'licensePlateNr',
    Country = 'country',
    Type = 'type',
    BadgeLabelNr = 'badgeLabelNr',
}

export type VehicleOnboardingPayload = {
    [Fields.Description]: string;
    [Fields.LicensePlate]: string | null;
    [Fields.Country]: string | null;
    [Fields.Type]: VehicleLicensePlateType | null;
    [Fields.BadgeLabelNr]: string | null;
};

interface VehicleFormProps {
    requestState: ServerRequestState<string, ValidationData>;
    submit: (formData: VehicleOnboardingPayload) => void;
    deleteVehicle?: () => void;
    vehicleDeleteState?: ServerRequestState<string, ValidationData>;
    countriesOptions: ReactHookFormSelectOption<string>[];
    loading?: boolean;
    isOnboarding?: boolean;
    style?: React.CSSProperties;
    readonly?: boolean;
    initialValues?: VehicleOnboardingPayload;
    hasOpenEntryWithoutExit?: boolean;
}

function getInitialValues(initialValues?: VehicleOnboardingPayload) {
    if (initialValues) {
        return {
            [Fields.Description]: initialValues.description,
            [Fields.LicensePlate]: initialValues.licensePlateNr,
            [Fields.Type]: initialValues.type,
            [Fields.Country]: initialValues.country,
            [Fields.BadgeLabelNr]: initialValues.badgeLabelNr,
        };
    }
    return {
        [Fields.Description]: '',
        [Fields.LicensePlate]: null,
        [Fields.Type]: VehicleLicensePlateType.CAR,
        [Fields.Country]: 'CH',
        [Fields.BadgeLabelNr]: null,
    };
}

export function VehicleForm({
    requestState,
    submit,
    deleteVehicle,
    vehicleDeleteState,
    countriesOptions,
    loading,
    isOnboarding,
    style,
    readonly,
    initialValues,
    hasOpenEntryWithoutExit,
}: VehicleFormProps) {
    const theme = useTheme();
    const { language } = useLanguage();
    const navigate = useNavigate();
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [editNotPossibleOpen, setEditNotPossibleOpen] = useState(false);
    const [deleteNotPossibleOpen, setDeleteNotPossibleOpen] = useState(false);
    const { user, logout } = useCustomerContext();
    const vehicleIdentification = user.role.vehicleIdentification;
    const isBadgeRequired = vehicleIdentification.includes(
        VehicleIdentification.BADGE,
    );
    const isLpRequired = vehicleIdentification.includes(
        VehicleIdentification.LICENSE_PLATE,
    );

    const {
        formInfo: { control, handleSubmit, reset, watch, setValue },
        formState: { isDirty },
        genericSubmitError,
    } = useEasyForm(undefined, requestState, language, {
        defaultValues: getInitialValues(initialValues),
    });

    const description = watch(Fields.Description);
    const lp = watch(Fields.LicensePlate);
    const badgeLabel = watch(Fields.BadgeLabelNr);
    const type = watch(Fields.Type);
    const country = watch(Fields.Country);
    const showLpFields = Boolean(lp);

    useEffect(() => {
        if (initialValues) {
            reset();
        }
    }, [initialValues, reset]);

    function handleEditCancel() {
        reset();
        navigate(-1);
    }

    function handleOnboardingCancel() {
        logout();
    }

    return (
        <>
            <FeedbackPopup
                open={deleteConfirmOpen}
                onAbort={() => setDeleteConfirmOpen(false)}
                onConfirm={() => {
                    setDeleteConfirmOpen(false);
                    if (deleteVehicle) {
                        deleteVehicle();
                    }
                }}
                abortLabel={
                    <Localized
                        de="Abbrechen"
                        fr="Annuler"
                        it="Annulla"
                        en="Cancel"
                    />
                }
                confirmLabel={
                    <Localized
                        de="Löschen"
                        fr="Effacer"
                        it="Elimina"
                        en="Delete"
                    />
                }
                title={
                    <Localized
                        de="Fahrzeug löschen"
                        fr="Supprimer véhicule"
                        it="Eliminare veicolo"
                        en="Delete vehicle"
                    />
                }
            >
                <Localized
                    de={`Wollen Sie wirklich das Fahrzeug «${description}» löschen?`}
                    fr={`Voulez-vous vraiment effacer le véhicule «${description}»?`}
                    it={`Vuole veramente eliminare il veicolo «${description}»?`}
                    en={`Do you really want to delete the vehicle «${description}»?`}
                />
            </FeedbackPopup>
            <FeedbackPopup
                color={'error'}
                open={editNotPossibleOpen}
                onConfirm={() => {
                    setEditNotPossibleOpen(false);
                }}
                confirmLabel={<Localized de="OK" fr="OK" it="OK" en="OK" />}
                title={
                    <Localized
                        de="Änderung nicht möglich"
                        fr="Modification pas possible"
                        it="Modifica non possibile"
                        en="Editing not possible"
                    />
                }
            >
                <>
                    <p>
                        <Localized
                            de="Für dieses Fahrzeug läuft derzeit ein Parkvorgang und kann daher in der App nicht geändert werden."
                            fr="Une transaction est en cours pour ce véhicule et il ne peut donc pas être modifié dans l'app."
                            it="Per questo veicolo c'é una transazione in corso e per questo non può essere modificato nell'app."
                            en="There is a transaction in progress for this vehicle, so it cannot be modified in the app."
                        />
                    </p>
                    <p>
                        <Localized
                            de="Für weitere Informationen wenden Sie sich bitte an den Betreiber:"
                            fr="Pour plus d'informations, veuillez contacter l'exploitant:"
                            it="Per ulteriori chiarimenti contatti il gestore:"
                            en="For further information, please contact the operator:"
                        />
                    </p>
                    <p>
                        <ParkingaboContactInfo
                            operatorName={user.operatorName}
                            parkingaboContact={user.parkingaboContact}
                        />
                    </p>
                </>
            </FeedbackPopup>
            <FeedbackPopup
                color={'error'}
                open={deleteNotPossibleOpen}
                onConfirm={() => {
                    setDeleteNotPossibleOpen(false);
                }}
                confirmLabel={<Localized de="OK" fr="OK" it="OK" en="OK" />}
                title={
                    <Localized
                        de="Löschung nicht möglich"
                        fr="Suppression impossible"
                        it="Eliminazione non possibile"
                        en="Deletion not possible"
                    />
                }
            >
                <>
                    <p>
                        <Localized
                            de="Für dieses Fahrzeug läuft derzeit ein Parkvorgang und kann daher in der App nicht gelöscht werden."
                            fr="Une transaction est en cours pour ce véhicule et il ne peut donc pas être supprimé dans l'app."
                            it="Per questo veicolo c'é una transazione in corso e per questo non può essere eliminato nell'app."
                            en="There is a transaction in progress for this vehicle, so it cannot be deleted in the app."
                        />
                    </p>
                    <p>
                        <Localized
                            de="Für weitere Informationen wenden Sie sich bitte an den Betreiber:"
                            fr="Pour plus d'informations, veuillez contacter l'exploitant:"
                            it="Per ulteriori chiarimenti contatti il gestore:"
                            en="For further information, please contact the operator:"
                        />
                    </p>
                    <p>
                        <ParkingaboContactInfo
                            operatorName={user.operatorName}
                            parkingaboContact={user.parkingaboContact}
                        />
                    </p>
                </>
            </FeedbackPopup>
            <Box
                sx={{
                    flexGrow: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexDirection: 'column',
                    ...style,
                }}
            >
                <Box>
                    <ReactHookFormTextField<VehicleOnboardingPayload>
                        sx={{
                            marginBottom: 3,
                        }}
                        control={control}
                        name={Fields.Description}
                        required={true}
                        label={
                            <Localized
                                de="Beschreibung"
                                fr="Description"
                                it="Descrizione"
                                en="Description"
                            />
                        }
                        readonly={readonly}
                    />

                    <LicensePlateFields
                        control={control}
                        language={language}
                        readonly={readonly}
                        showLpFields={showLpFields}
                        isLpRequired={isLpRequired}
                        countriesOptions={countriesOptions}
                        type={type}
                        country={country}
                        setValue={setValue}
                    />

                    <BadgeFields
                        control={control}
                        language={language}
                        readonly={readonly}
                        isBadgeRequired={isBadgeRequired}
                        vehicleIdentification={user.role.vehicleIdentification}
                    />

                    <GenericFormSubmitError
                        submitState={requestState}
                        error={genericSubmitError}
                    />
                    {vehicleDeleteState && (
                        <BackendRequestErrorMessage
                            requestState={vehicleDeleteState}
                        />
                    )}
                    {isOnboarding && (
                        <Alert
                            color="info"
                            style={{ marginBottom: theme.spacing(2) }}
                        >
                            <Typography>
                                <Localized
                                    de="Nach der Registrierung können weitere Fahrzeuge direkt im Konto erfasst und verwaltet werden."
                                    fr="Après l'enregistrement, d'autres véhicules peuvent être saisis et gérés directement dans le compte."
                                    it="Dopo la registrazione, è possibile aggiungere e gestire altri veicoli direttamente nel conto."
                                    en="After registration, additional vehicles can be added and managed directly in the account."
                                />
                            </Typography>
                        </Alert>
                    )}
                </Box>
                {!readonly && (isDirty || !deleteVehicle) && (
                    <CancelSave
                        onSave={() => {
                            if (
                                hasOpenEntryWithoutExit &&
                                (initialValues?.badgeLabelNr !== badgeLabel ||
                                    initialValues?.licensePlateNr !== lp ||
                                    initialValues?.country !== country ||
                                    initialValues?.type !== type)
                            ) {
                                setEditNotPossibleOpen(true);
                            } else {
                                handleSubmit(submit)();
                            }
                        }}
                        onCancel={
                            !isOnboarding
                                ? handleEditCancel
                                : handleOnboardingCancel
                        }
                        saveDisabled={
                            !isDirty ||
                            description.length === 0 ||
                            (isLpRequired && !lp) ||
                            (isBadgeRequired && !badgeLabel)
                        }
                        requestState={requestState}
                        loading={loading}
                    />
                )}
                {!readonly && !isDirty && deleteVehicle && (
                    <Button
                        loading={
                            vehicleDeleteState?.status === RequestStatus.PENDING
                        }
                        variant={'outlined'}
                        onClick={() => {
                            if (hasOpenEntryWithoutExit) {
                                setDeleteNotPossibleOpen(true);
                            } else {
                                setDeleteConfirmOpen(true);
                            }
                        }}
                    >
                        <Localized
                            de="Löschen"
                            fr="Supprimer"
                            it="Elimina"
                            en="Delete"
                        />
                    </Button>
                )}
            </Box>
        </>
    );
}

function LicensePlateFields({
    control,
    language,
    readonly,
    showLpFields,
    isLpRequired,
    countriesOptions,
    type,
    country,
    setValue,
}: {
    control: Control<VehicleOnboardingPayload, object>;
    language: Language;
    readonly?: boolean;
    showLpFields: boolean;
    isLpRequired: boolean;
    countriesOptions: ReactHookFormSelectOption<string>[];
    type: VehicleLicensePlateType | null;
    country: string | null;
    setValue: UseFormSetValue<VehicleOnboardingPayload>;
}) {
    const mandatoryFieldMessage = useTranslation(mandatoryFieldErrorMessage);
    return (
        <Grid container sx={{ marginBottom: 3 }}>
            <Grid size={5}>
                <ReactHookFormTextField<VehicleOnboardingPayload>
                    control={control}
                    name={Fields.LicensePlate}
                    required={isLpRequired}
                    label={
                        <Localized
                            de="Kennzeichen"
                            fr="Immatriculation"
                            it="Targa"
                            en="License plate"
                        />
                    }
                    forceUppercase
                    readonly={readonly}
                    onChange={event => {
                        if (event.target.value) {
                            if (!type) {
                                setValue(
                                    Fields.Type,
                                    VehicleLicensePlateType.CAR,
                                );
                            }
                            if (!country) {
                                setValue(Fields.Country, 'CH');
                            }
                        } else {
                            setValue(Fields.Type, null);
                            setValue(Fields.Country, null);
                        }
                    }}
                />
            </Grid>

            {showLpFields && (
                <>
                    <Grid sx={{ paddingLeft: '10px', flexGrow: 1 }}>
                        <ReactHookFormDropdownSelect<
                            VehicleOnboardingPayload,
                            string
                        >
                            control={control}
                            name={Fields.Country}
                            rules={{
                                required: {
                                    value: isLpRequired,
                                    message: mandatoryFieldMessage,
                                },
                            }}
                            label={
                                <Localized
                                    de="Land"
                                    fr="Pays"
                                    it="Nazione"
                                    en="Country"
                                />
                            }
                            options={countriesOptions}
                            readonly={readonly}
                        />
                    </Grid>
                    <Grid sx={{ paddingLeft: '10px', flexGrow: 1 }}>
                        <ReactHookFormDropdownSelect<
                            VehicleOnboardingPayload,
                            VehicleLicensePlateType
                        >
                            control={control}
                            name={Fields.Type}
                            rules={{
                                required: {
                                    value: isLpRequired,
                                    message: mandatoryFieldMessage,
                                },
                            }}
                            label={
                                <Localized
                                    de="Fahrzeugtyp"
                                    fr="Type de véhicule"
                                    it="Tipo veicolo"
                                    en="Vehicle type"
                                />
                            }
                            options={licensePlateOptions(language)}
                            readonly={readonly}
                        />
                    </Grid>
                </>
            )}
        </Grid>
    );
}

function BadgeFields({
    control,
    language,
    readonly,
    isBadgeRequired,
    vehicleIdentification,
}: {
    control: Control<VehicleOnboardingPayload, object>;
    language: Language;
    readonly?: boolean;
    isBadgeRequired: boolean;
    vehicleIdentification: VehicleIdentification[];
}) {
    if (vehicleIdentification.includes(VehicleIdentification.BADGE)) {
        return (
            <Grid container sx={{ marginY: 3 }}>
                <Grid size={6}>
                    <ReactHookFormTextField<VehicleOnboardingPayload>
                        name={Fields.BadgeLabelNr}
                        control={control}
                        label={
                            <Localized
                                de="Badgenummer"
                                fr="Numéro badge"
                                it="Numero badge"
                                en="Badge number"
                            />
                        }
                        rules={{
                            validate: {
                                rfidFormat: (value: string) => {
                                    if (value) {
                                        return (
                                            parkingaboBadgeErrorText(
                                                value,
                                                language,
                                            ) || true
                                        );
                                    }
                                },
                            },
                        }}
                        required={isBadgeRequired}
                        readonly={readonly}
                    />
                </Grid>
            </Grid>
        );
    }

    return null;
}
